import { ArrowRight, X } from "@phosphor-icons/react";
import { type CSSProperties, type ReactNode, useEffect, useId, useState } from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../lib/useReducedMotion";
import { Button } from "../primitives/button";
import { Modal, ModalDescription, ModalTitle } from "../primitives/modal";

export interface PageExplainerStageContext {
  stepIndex: number;
  reducedMotion: boolean;
}

export interface PageExplainerStep {
  id: string;
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  caption?: ReactNode;
  renderStage: (context: PageExplainerStageContext) => ReactNode;
}

export interface PageExplainerProps {
  open: boolean;
  onDismiss: () => void;
  /** Reaching the end, as opposed to leaving. */
  onDone?: () => void;
  steps: ReadonlyArray<PageExplainerStep>;
  closeLabel?: string;
  skipLabel?: string;
  nextLabel?: string;
  doneLabel?: string;
  contentClassName?: string;
  stageClassName?: string;
  /** Modal width step; explainers default to xl. */
  size?: "lg" | "xl";
  /** Reduces the explainer's visual density for focused product tours. */
  compact?: boolean;
}

export function PageExplainer({
  open,
  onDismiss,
  onDone,
  steps,
  closeLabel = "Close explainer",
  skipLabel = "Skip",
  nextLabel = "Continue",
  doneLabel = "Got it",
  contentClassName,
  stageClassName,
  size = "xl",
  compact = false,
}: PageExplainerProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const reducedMotion = useReducedMotion();
  const progressLabelId = useId();

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  useEffect(() => {
    if (steps.length === 0) return;
    setStepIndex((current) => Math.min(current, steps.length - 1));
  }, [steps.length]);

  useEffect(() => {
    if (!open || steps.length < 2) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input,textarea,select,[contenteditable='true']")) return;

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setStepIndex((current) => Math.min(current + 1, steps.length - 1));
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setStepIndex((current) => Math.max(current - 1, 0));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, steps.length]);

  if (steps.length === 0) return null;

  const activeIndex = Math.min(stepIndex, steps.length - 1);
  const current = steps[activeIndex];
  if (!current) return null;
  const isLast = activeIndex === steps.length - 1;
  const stepNumber = String(activeIndex + 1).padStart(2, "0");
  const stepTotal = String(steps.length).padStart(2, "0");
  const textRevealStyle: CSSProperties | undefined = reducedMotion
    ? undefined
    : {
        animation: "page-explainer-text-in 480ms var(--ease-out-expo) both",
      };

  const goNext = () => {
    if (isLast) {
      (onDone ?? onDismiss)();
      return;
    }
    setStepIndex((next) => Math.min(next + 1, steps.length - 1));
  };

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss();
      }}
      size={size}
      contentClassName={cn(
        "max-h-[92vh] overflow-hidden rounded-modal bg-paper p-0",
        contentClassName,
      )}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={closeLabel}
        className={cn(
          "absolute right-5 top-5 z-20 grid place-items-center rounded-full border border-mist bg-paper/90 text-gray-500 shadow-card transition-colors hover:border-mist-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-paper",
          compact ? "h-9 w-9" : "h-11 w-11",
        )}
      >
        <X size={20} weight="bold" aria-hidden="true" />
      </button>

      <div className="max-h-[92vh] overflow-y-auto">
        <section
          key={current.id}
          className={cn(
            "relative overflow-hidden",
            compact
              ? "min-h-[250px] px-5 pb-5 pt-6 md:min-h-[280px] md:px-8"
              : "min-h-[300px] px-6 pb-6 pt-8 md:min-h-[340px] md:px-10",
            stageClassName,
          )}
          style={{
            background:
              "radial-gradient(120% 80% at 50% 110%, color-mix(in srgb, var(--color-teal-600) 10%, transparent) 0%, transparent 60%), linear-gradient(160deg, var(--color-cream-warm) 0%, var(--color-cream-deep) 100%)",
          }}
        >
          <div
            className={cn(
              "relative flex items-center justify-center",
              compact
                ? "min-h-[180px] pt-2 md:min-h-[210px]"
                : "min-h-[220px] pt-4 md:min-h-[250px]",
            )}
          >
            {current.renderStage({ stepIndex: activeIndex, reducedMotion })}
          </div>
          {current.caption ? (
            <div
              className={cn(
                "relative flex items-center justify-center gap-2 font-mono font-semibold uppercase tracking-[0.18em] text-gray-500",
                compact ? "pt-4 text-2xs md:pt-5" : "pt-6 text-xs md:pt-8",
              )}
            >
              <span
                className="h-2 w-2 rounded-full bg-teal-600 shadow-[0_0_0_5px_color-mix(in_srgb,var(--color-teal-600)_16%,transparent)]"
                aria-hidden="true"
              />
              {current.caption}
            </div>
          ) : null}
        </section>

        <section className={compact ? "px-6 py-6 md:px-8 md:py-7" : "px-7 py-7 md:px-12 md:py-10"}>
          <div
            className={cn(
              "flex flex-wrap items-center font-mono font-semibold uppercase tracking-[0.18em] text-teal-700",
              compact ? "mb-3 gap-2 text-2xs" : "mb-4 gap-3 text-xs",
            )}
          >
            <span className="h-2 w-2 rounded-full bg-teal-600" aria-hidden="true" />
            <span>{current.eyebrow}</span>
            <span className="text-gray-400" aria-hidden="true">
              .
            </span>
            <span>
              {stepNumber} of {stepTotal}
            </span>
          </div>

          <ModalTitle asChild>
            <h2
              key={`title-${current.id}`}
              className={cn(
                "m-0 max-w-[17ch] font-title font-medium leading-[1.08] tracking-tight text-navy [&_em]:font-normal [&_em]:italic [&_em]:text-teal-700",
                compact ? "text-3xl md:text-4xl" : "text-[38px] md:text-[52px]",
              )}
              style={textRevealStyle}
            >
              {current.title}
            </h2>
          </ModalTitle>

          <ModalDescription asChild>
            <p
              key={`description-${current.id}`}
              className={cn(
                "m-0 max-w-[62ch] leading-[1.65] text-ink-secondary",
                compact ? "mt-4 text-base md:text-lg" : "mt-6 text-[17px] md:text-[20px]",
              )}
              style={
                reducedMotion
                  ? undefined
                  : {
                      animation: "page-explainer-text-in 480ms var(--ease-out-expo) 120ms both",
                    }
              }
            >
              {current.description}
            </p>
          </ModalDescription>

          <div
            className={cn(
              "flex flex-col border-t border-mist sm:flex-row sm:items-center sm:justify-between",
              compact ? "mt-6 gap-3 pt-5" : "mt-9 gap-5 pt-6",
            )}
          >
            <div
              className="flex items-center gap-3"
              role="tablist"
              aria-labelledby={progressLabelId}
            >
              <span id={progressLabelId} className="sr-only">
                Explainer step
              </span>
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`Step ${index + 1}`}
                  onClick={() => setStepIndex(index)}
                  className={cn(
                    "rounded-full bg-mist-dark transition-[width,background-color] duration-200 ease-out hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-paper",
                    // The bar stays thin; the pointer target grows vertically to clear 24px (2.5.8).
                    "relative after:absolute after:inset-x-0 after:-inset-y-[9px] after:content-['']",
                    compact ? "h-1.5" : "h-2",
                    index === activeIndex
                      ? compact
                        ? "w-11 bg-teal-600"
                        : "w-16 bg-teal-600"
                      : compact
                        ? "w-6"
                        : "w-10",
                    index < activeIndex ? "bg-teal-300" : null,
                  )}
                />
              ))}
            </div>

            <div className="flex items-center justify-end gap-3">
              {!isLast ? (
                <Button
                  variant="link"
                  size={compact ? "md" : "lg"}
                  onClick={onDismiss}
                  className={compact ? "px-3 text-base" : "px-4 text-[17px]"}
                >
                  {skipLabel}
                </Button>
              ) : null}
              <Button
                variant="navy"
                size={compact ? "md" : "lg"}
                onClick={goNext}
                className={
                  compact ? "px-5 normal-case tracking-tight" : "px-7 normal-case tracking-tight"
                }
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: compact ? "var(--text-base)" : "17px",
                  fontWeight: 500,
                  letterSpacing: "var(--tracking-body)",
                  textTransform: "none",
                }}
                rightIcon={
                  !isLast ? <ArrowRight size={18} weight="bold" aria-hidden="true" /> : null
                }
              >
                {isLast ? doneLabel : nextLabel}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
}
